import React, { useState } from 'react';
import { Building2, Package, MapPin, Home, ExternalLink, CloudSun, BedDouble } from 'lucide-react';
import { SitesPage } from './SitesPage';
import { WasteTrackingPage } from './WasteTrackingPage';
import { WasteDisposalFacilitiesPage } from './WasteDisposalFacilitiesPage';
import { WeatherPage } from './WeatherPage';
import { AccommodationPage } from './AccommodationPage';

type Tab = 'sites' | 'waste-tracking' | 'waste-facilities' | 'dilapidation' | 'weather' | 'accommodation';

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }>; description: string; external?: string }[] = [
  { id: 'sites',            label: 'Sites',                      icon: Building2, description: 'Manage all active job sites. Add new sites, view GPS locations, track project status, and manage client information.' },
  { id: 'waste-tracking',   label: 'Waste Tracking',             icon: Package,   description: 'Record and track waste removed from job sites. Log waste type, weight, disposal facility, and associated costs.' },
  { id: 'waste-facilities', label: 'Waste Disposal Facilities',  icon: MapPin,    description: 'Manage approved waste disposal facilities and their accepted waste types, pricing, and asbestos handling conditions.' },
  { id: 'dilapidation',     label: 'Dilapidation Reports',       icon: Home,      description: 'Open the Dilapidation Reports tool to create and manage pre- and post-works property condition reports.', external: 'https://dilapidation.bolt.host/' },
  { id: 'weather',          label: 'Weather',                    icon: CloudSun,  description: 'View 7-day weather forecasts and rainfall history for all active sites. Rain alerts are highlighted when over 10mm is expected.' },
  { id: 'accommodation',   label: 'Accommodation',               icon: BedDouble, description: 'Search Airbnb for short-stay accommodation near your active job sites. Pick a site, choose your dates, and open Airbnb pre-filled.' },
];

export const SitesManagementPage: React.FC = () => {
  const [active, setActive] = useState<Tab>('sites');

  const handleTabClick = (tab: typeof TABS[number]) => {
    if (tab.external) {
      window.open(tab.external, '_blank', 'noopener,noreferrer');
      return;
    }
    setActive(tab.id);
  };

  return (
    <div className="space-y-0">
      <div className="bg-white border-b border-gray-200 -mx-6 px-6 mb-6">
        <nav className="flex gap-1" aria-label="Sites sections">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = active === tab.id;
            return (
              <div key={tab.id} className="relative group">
                <button
                  onClick={() => handleTabClick(tab)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    tab.external
                      ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      : isActive
                        ? 'border-orange-500 text-orange-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                  {tab.external && <ExternalLink className="h-3 w-3 opacity-60" />}
                </button>
                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 hidden group-hover:block">
                  <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl w-56 leading-snug text-center">
                    {tab.description}
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full border-4 border-transparent border-b-gray-900" />
                  </div>
                </div>
              </div>
            );
          })}
        </nav>
      </div>

      <div className={active === 'sites'            ? '' : 'hidden'}><SitesPage /></div>
      <div className={active === 'waste-tracking'   ? '' : 'hidden'}><WasteTrackingPage /></div>
      <div className={active === 'waste-facilities' ? '' : 'hidden'}><WasteDisposalFacilitiesPage /></div>
      <div className={active === 'weather'         ? '' : 'hidden'}><WeatherPage /></div>
      <div className={active === 'accommodation'   ? '' : 'hidden'}><AccommodationPage /></div>
    </div>
  );
};




